import type { Value } from '../../types/global';
import {
  recordResponses,
  setDisplayFullQueue,
  addToTotalQueue,
  setInterlocuter,
} from '@/reducers/graph';
import { ViewableResponse } from '@/types/global';
import { RootState } from '@/types/graph';
import { useApolloClient } from '@apollo/client';
import { useList } from '@uidotdev/usehooks';
import { compact } from 'lodash';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useCreatePatient, useSelectPatient } from '../patient.hooks';
import { useUpdateQuestionnaire } from '../questionnaire.hooks';
import { useInjectConsultation } from '../useInjectConsultation';
import { useInjectInterlocuter } from '../useInjectInterlocuter';
import { useQuestionnaireNavigation } from '../../context/NavigationProvider';
import { useResetQuestionnaire } from '../useResetQuestionnaire';
import { Scenario } from '@/types/scenario';
import { SHARED_ROUTES } from '../../routes';
import {
  patientResponseIds,
  responseIds,
} from '@aldebaran/common/constants/kb';
import { useActiveQueue } from '../useActiveQueue';
import { setRunActive } from './runActive';
import {
  filterOnboardingQuestions,
  isQuestionnairePageEmpty,
} from '../../util/questionnaire';

type Step =
  | 'INIT'
  | 'PATIENT'
  | 'CONSULTATION'
  | 'AWAIT_PAGE'
  | 'PRE_QUESTION'
  | 'QUESTION'
  | 'SETTLE';

export const isFastTrackScenario = (scenario?: Scenario): boolean =>
  scenario?.responses.length === 0;

export type PreQuestionAction =
  | 'COMPLETE'
  | 'AWAIT_PAGE'
  | 'SUBMIT'
  | 'QUESTION';

export const getPreQuestionAction = (
  scenario: Scenario,
  currentPageIndex: number,
): PreQuestionAction => {
  if (isFastTrackScenario(scenario)) return 'COMPLETE';

  const currentPageData = scenario.responses[currentPageIndex];
  if (!currentPageData || !Array.isArray(currentPageData)) {
    return scenario.responses[currentPageIndex + 1] !== undefined
      ? 'AWAIT_PAGE'
      : 'SUBMIT';
  }

  if (currentPageData.length === 0) {
    return scenario.responses[currentPageIndex + 1] !== undefined
      ? 'AWAIT_PAGE'
      : 'SUBMIT';
  }

  return 'QUESTION';
};

export const useRunScenario = (scenario?: Scenario) => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const reset = useResetQuestionnaire();
  const store = useStore();
  const [log, { set: setLog, push: pushRawLog }] = useList<string>([]);
  const pushLog = useCallback(
    (message: string) => {
      pushRawLog(message);
    },
    [pushRawLog],
  );
  const [step, setStep] = useState<Step | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [delayMs, setDelayMs] = useState(0);

  const { mutateAsync: createPatient } = useCreatePatient(true);
  const { mutateAsync: selectPatient } = useSelectPatient();
  const injectConsultation = useInjectConsultation();
  const injectInterlocuter = useInjectInterlocuter();
  const { navigateNext } = useQuestionnaireNavigation();
  const navigateNextRef = useRef(navigateNext);
  useEffect(() => {
    navigateNextRef.current = navigateNext;
  }, [navigateNext]);
  const awaitingFinalSubmission = useRef(false);
  const activeQueue = useActiveQueue();
  // Mirrors whether the active scenario is fast-track so the mutation
  // callback below can decide without depending on closure freshness.
  const isFastTrackRef = useRef(false);
  useEffect(() => {
    isFastTrackRef.current = isFastTrackScenario(scenario);
  }, [scenario]);
  const { updateQuestionnaire } = useUpdateQuestionnaire({
    onSuccess: () => {
      if (currentPage !== 'INTRODUCTION') {
        dispatch(addToTotalQueue({ currentPage, queue: activeQueue }));
      }
      if (awaitingFinalSubmission.current) {
        awaitingFinalSubmission.current = false;
        pushLog('Final submission saved');
        setIsRunning(false);
      }
      // A fast-track run owns navigation: its AWAIT_PAGE chain advances
      // through the intro pages and lands on the first questionnaire page by
      // itself. Firing navigateNext here — when the introduction mutation
      // resolves — races that chain: if the response arrives after the run
      // has already landed on /questionnaire/<first>, navigateNext sees a
      // questionnaire route and pushes the NEXT page (observed live:
      // consultation → complaints, flaky with network timing).
      if (!isFastTrackRef.current) {
        navigateNext();
      }
    },
  });
  const dispatch = useDispatch();
  const updateQuestionnaireRef = useRef(updateQuestionnaire);

  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );

  useEffect(() => {
    updateQuestionnaireRef.current = updateQuestionnaire;
  }, [updateQuestionnaire]);

  const delayRef = useRef(delayMs);
  useEffect(() => {
    delayRef.current = delayMs;
  }, [delayMs]);

  const stepVersionRef = useRef(0);
  const stepRef = useRef<Step | null>(null);
  // Last non-questionnaire pathname the AWAIT_PAGE effect already advanced
  // past. The effect re-runs on every location/step/isRunning change and its
  // else-branch calls navigateNext() WITHOUT advancing the step, so a re-render
  // that still sees the stale location would push one page too many (observed
  // live: steps-overview → consultation → complaints from a single submit).
  const awaitedPathRef = useRef<string | null>(null);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const poll = useCallback(
    <T,>(
      check: () => T | undefined,
      timeoutMs: number,
      errorMsg: string,
    ): Promise<T> => {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const tick = () => {
          try {
            const result = check();
            if (result !== undefined) {
              resolve(result);
            } else if (Date.now() - startTime > timeoutMs) {
              reject(new Error(errorMsg));
            } else {
              setTimeout(tick, 100);
            }
          } catch (error) {
            reject(error);
          }
        };
        tick();
      });
    },
    [],
  );

  const waitForPatientResponses = useCallback(
    (timeoutMs = 10000) =>
      poll(
        () => {
          const responses = (store.getState() as RootState).kb.responses;
          return patientResponseIds.every((id) => responses[id])
            ? true
            : undefined;
        },
        timeoutMs,
        `Timeout waiting for patient KB responses`,
      ),
    [store, poll],
  );

  const waitForQuestion = useCallback(
    (questionId: string, timeoutMs = 30000) =>
      poll(
        () => {
          const q = (store.getState() as RootState).kb.questions[questionId];
          return q && q.responses.length > 0 ? true : undefined;
        },
        timeoutMs,
        `Timeout waiting for question ${questionId}`,
      ),
    [store, poll],
  );

  /**
   * Resolves once the LANDING questionnaire page (the first entry of
   * `config.pages`) holds a KB-backed, displayable question.
   *
   * Live evidence from the deployed preview (state mirrored by the fast-track
   * panel): the traitant question ("Le Dr … est-il votre médecin traitant ?")
   * only enters the CONSULTATION page's CONTEXTE queue AFTER the introduction
   * is submitted — `updateQuestionnaire` triggers a KB refresh that delivers
   * it (observed kb 42 → 45 → 48 while `CONSULTATION:dq=0` flipped to
   * `dq=1:first=22(kb)`). Before that submission the queue contains only the
   * onboarding questions (CONSULTATION_DATE / CONSULTATION_TIME), which
   * `filterOnboardingQuestions` strips, so the page reads as empty and
   * `Questionnaire`'s auto-skip walks the app forward (drift to PREVENTION /
   * CONCLUSION).
   *
   * Two consequences baked into this predicate:
   * - It targets the FIRST config page specifically. An earlier iteration
   *   iterated pages and resolved on the first non-empty one — which matched
   *   PREVENTION's header questions and let runs drift while "passing".
   * - Callers must invoke it AFTER `updateQuestionnaireRef.current()`, never
   *   before: pre-submission the landing page is legitimately empty and the
   *   gate can only time out.
   *
   * The emptiness predicate is shared with `Questionnaire` via
   * `isQuestionnairePageEmpty`, so the gate and the auto-skip cannot disagree.
   */
  const waitForFirstQuestionnairePage = useCallback(
    (timeoutMs = 30000) =>
      poll(
        () => {
          const state = store.getState() as RootState;
          const pages = state.questionnaire.config?.pages;
          const landingPage = pages ? Object.keys(pages)[0] : undefined;
          if (!landingPage || !pages) return undefined;

          const sections = pages[landingPage]?.sections || [];
          const activePageQueue = sections.flatMap(
            (section) => state.questionnaire.sectionQueues[section] || [],
          );
          const displayQueue = filterOnboardingQuestions(
            activePageQueue,
            landingPage,
          );
          // The first displayable question must exist in the KB — a queue id
          // without a KB entry renders nothing and would resolve the gate on
          // a page the patient cannot actually see.
          const currentQuestion = state.kb.questions[displayQueue[0]];
          if (!currentQuestion) return undefined;

          return isQuestionnairePageEmpty(displayQueue, currentQuestion)
            ? undefined
            : true;
        },
        timeoutMs,
        'Timeout waiting for the landing questionnaire page to hold a KB-backed question',
      ),
    [poll, store],
  );

  const waitForPatientQuery = useCallback(
    (patientId: string, timeoutMs = 10000) =>
      poll(
        () => queryClient.getQueryData(['patient', patientId]) ?? undefined,
        timeoutMs,
        `Timeout waiting for patient query data (id: ${patientId})`,
      ),
    [queryClient, poll],
  );

  const waitForFormulas = useCallback(
    (timeoutMs = 15000) =>
      poll(
        () => {
          const formulasLoaded = (store.getState() as RootState).kb
            .formulasLoaded;
          return formulasLoaded ? true : undefined;
        },
        timeoutMs,
        'Timeout waiting for formulas to load from KB',
      ),
    [store, poll],
  );

  /**
   * Resolves once the KB has delivered the INTERLOCUTER response definitions.
   * `useInjectInterlocuter` bails silently when they are missing, so the
   * runner must hold until they exist before mirroring the self-identification
   * step — otherwise the injection is silently dropped and the landing page's
   * traitant question never becomes valid.
   */
  const waitForInterlocuterResponses = useCallback(
    (timeoutMs = 15000) =>
      poll(
        () => {
          const responses = (store.getState() as RootState).kb.responses;
          return responses[responseIds.INTERLOCUTER.SELF] &&
            responses[responseIds.INTERLOCUTER.OTHER]
            ? true
            : undefined;
        },
        timeoutMs,
        'Timeout waiting for INTERLOCUTER response definitions from KB',
      ),
    [store, poll],
  );

  const answer = useCallback(
    ({
      questionId,
      currentPage,
      currentSection,
      responses,
    }: {
      questionId: string;
      currentPage: string;
      currentSection: string;
      responses: {
        [key: string]: Value;
      };
    }) => {
      const element = document.getElementById('question_' + questionId);
      element?.scrollIntoView({ behavior: 'smooth' });

      const currentQuestions = (store.getState() as RootState).kb.questions;
      const question = currentQuestions[questionId];
      if (!question) {
        pushLog(`Warning: question ${questionId} not found in store`);
        return;
      }

      dispatch(
        recordResponses({
          questionId,
          currentPage,
          currentSection,
          responses: compact(
            Object.entries(responses).map(([key, value]) => {
              if (value === undefined || value === null) return undefined;
              if (typeof value === 'boolean' && !value) return undefined;

              const response = question.responses.find((r) => r.extId === key);
              if (!response) {
                pushLog(
                  `Warning: response ${key} not found for Q#${questionId}`,
                );
                return undefined;
              }

              return {
                ...(response as ViewableResponse),
                value: { value, unit: response.unit },
              };
            }),
          ),
        }),
      );
    },
    [dispatch, store, pushLog],
  );

  const fail = useCallback(
    (msg: string) => {
      setRunActive(false);
      pushLog(`Error: ${msg}`);
      setStep(null);
      setIsRunning(false);
    },
    [pushLog],
  );

  const run = useCallback(() => {
    setLog(['Run']);
    setCurrentPageIndex(0);
    setIsRunning(true);
    stepVersionRef.current++;
    setStep('INIT');
  }, []);

  const stop = useCallback(() => {
    setRunActive(false);
    setStep(null);
    setIsRunning(false);
    pushLog('Stopped');
  }, [pushLog]);

  const advanceStep = useCallback((nextStep: Step) => {
    stepVersionRef.current++;
    setStep(nextStep);
  }, []);

  const initStep = useCallback(async () => {
    pushLog('Init');
    // Raise the run-active flag before anything navigates: from here until
    // settle/fail/stop, `Questionnaire`'s empty-page auto-skip stands down
    // because every empty-page condition during a run is transient (queue
    // rebuilds on landing) and the runner owns navigation.
    setRunActive(isFastTrackScenario(scenario));
    // Soft reset: the run keeps driving this page (config, Apollo, navigation)
    // right after, so it can't afford the reload a variant-carrying reset would
    // otherwise trigger.
    await reset({ softNavigate: true });
    // Force the app back to the onboarding flow before the AWAIT_PAGE location
    // effect can fire. Without this, a run that starts while the app is still
    // parked on a /questionnaire/* route from a previous run would skip the
    // patient/consultation steps entirely: the effect sees the location already
    // matches /questionnaire/* and jumps straight to PRE_QUESTION, so the
    // SETTLE drift detector fires on a stale pathname instead of the one this
    // run actually navigated to.
    navigate(SHARED_ROUTES.LANDING_PROFESSIONAL_SELECT);
    dispatch(setDisplayFullQueue(true));
    await new Promise((r) => setTimeout(r, 100));
    await apolloClient.resetStore();
    await waitForPatientResponses();
    pushLog('KB responses ready');
    await waitForFormulas();
    pushLog('Formulas loaded');
    advanceStep('PATIENT');
  }, [
    pushLog,
    apolloClient,
    reset,
    dispatch,
    navigate,
    scenario,
    waitForPatientResponses,
    waitForFormulas,
    advanceStep,
  ]);

  const patientStep = useCallback(async () => {
    if (!scenario) return;
    pushLog('Setting Patient');
    navigate(SHARED_ROUTES.PATIENT_INFORMATION);
    // Mirror the self-identification step the fast-track skips. The manual
    // page dispatches setInterlocuter and records the INTERLOCUTER KB
    // responses, which is what makes the landing page's traitant question
    // valid and delivers it into CONTEXTE — without it the landing page's
    // displayQueue stays empty forever and the run cannot rest there.
    // `useInjectInterlocuter` bails silently when the KB hasn't delivered
    // the response definitions yet, so wait for them before injecting.
    const self = scenario.patient.self;
    dispatch(setInterlocuter(self ? 'SELF' : 'OTHER'));
    await waitForInterlocuterResponses();
    injectInterlocuter(self);
    await waitForPatientResponses();
    const patient = await createPatient(scenario.patient);
    await selectPatient(patient.id);
    await waitForPatientQuery(patient.id);
    await waitForQuestion('CONSULTATION_TIME');
    navigate(SHARED_ROUTES.CONSULTATION_TIME);
    advanceStep('CONSULTATION');
  }, [
    pushLog,
    navigate,
    dispatch,
    waitForInterlocuterResponses,
    injectInterlocuter,
    waitForPatientResponses,
    waitForPatientQuery,
    waitForQuestion,
    createPatient,
    scenario,
    selectPatient,
    advanceStep,
  ]);

  const consultationStep = useCallback(async () => {
    if (!scenario) return;
    pushLog('Setting Consultation Date');

    injectConsultation({ date: scenario.consultation.date });

    if (isFastTrackScenario(scenario)) {
      // Submit the introduction BEFORE waiting on the landing page: the
      // submission is what triggers the KB refresh that delivers the landing
      // page's first real question (see the gate's doc comment). Waiting
      // pre-submission can only time out — the queue legitimately holds just
      // the filtered-out onboarding questions at that point.
      pushLog('Submitting introduction');
      advanceStep('AWAIT_PAGE');
      updateQuestionnaireRef.current();
      await waitForFirstQuestionnairePage();
      return;
    }

    const firstQ = scenario.responses[currentPageIndex]?.[0];
    if (!firstQ) {
      fail('No questions in first page of scenario');
      return;
    }
    await waitForQuestion(firstQ.questionId);

    pushLog('Submitting introduction');
    advanceStep('AWAIT_PAGE');
    updateQuestionnaireRef.current();
  }, [
    pushLog,
    scenario,
    injectConsultation,
    waitForQuestion,
    waitForFirstQuestionnairePage,
    currentPageIndex,
    advanceStep,
    fail,
  ]);

  const preQuestionStep = useCallback(() => {
    if (!scenario) return;

    const action = getPreQuestionAction(scenario, currentPageIndex);
    if (action === 'COMPLETE') {
      advanceStep('SETTLE');
      return;
    }

    if (action === 'AWAIT_PAGE' || action === 'SUBMIT') {
      if (scenario.responses[currentPageIndex + 1] !== undefined) {
        setCurrentPageIndex(currentPageIndex + 1);
        advanceStep('AWAIT_PAGE');
        updateQuestionnaireRef.current();
      } else {
        pushLog('Last page (empty) - submitting');
        awaitingFinalSubmission.current = true;
        updateQuestionnaireRef.current();
      }
      return;
    }

    advanceStep('QUESTION');
  }, [scenario, currentPageIndex, advanceStep, pushLog]);

  const settleStep = useCallback(async () => {
    const settledPathname = pathnameRef.current;
    let consecutiveChecks = 0;

    // A fast-track run may only call itself finished once the landing page
    // holds its first KB-backed question — the same resting state a manual
    // click-through reaches. The introduction submission triggers the KB
    // refresh that delivers it; until it lands, the empty-page auto-skip can
    // still walk the app away, so completing early would mask drift.
    if (scenario && isFastTrackScenario(scenario)) {
      await waitForFirstQuestionnairePage();
    }

    await poll(
      () => {
        const pathname = pathnameRef.current;
        if (!pathname.startsWith('/questionnaire/')) {
          throw new Error(`Fast-track route drifted to ${pathname}`);
        }
        if (pathname !== settledPathname) {
          throw new Error(`Fast-track route drifted to ${pathname}`);
        }
        consecutiveChecks += 1;
        return consecutiveChecks >= 3 ? true : undefined;
      },
      30000,
      `Timeout settling at ${settledPathname}`,
    );
    pushLog(`Fast-track complete at ${pathnameRef.current}`);
    setRunActive(false);
    setStep(null);
    setIsRunning(false);
  }, [poll, pushLog, scenario, waitForFirstQuestionnairePage]);

  const questionStep = useCallback(async () => {
    if (!scenario) return;

    const pageData = scenario.responses[currentPageIndex];
    if (!pageData || !Array.isArray(pageData)) return;

    for (let qi = 0; qi < pageData.length; qi++) {
      const qEntry = pageData[qi];

      await waitForQuestion(qEntry.questionId);

      if (delayRef.current > 0) {
        await new Promise((r) => setTimeout(r, delayRef.current));
      }

      answer(qEntry);
      pushLog(`Answered #${qEntry.questionId}`);
    }

    // Wait for React to re-render so derived values (report,
    // questionnaireSummary) recompute and updateQuestionnaireRef refreshes
    await new Promise((r) => setTimeout(r, 150));

    if (scenario.responses[currentPageIndex + 1] !== undefined) {
      setCurrentPageIndex(currentPageIndex + 1);
      advanceStep('AWAIT_PAGE');
      updateQuestionnaireRef.current();
    } else {
      pushLog('Last page - submitting');
      awaitingFinalSubmission.current = true;
      updateQuestionnaireRef.current();
    }
  }, [
    scenario,
    currentPageIndex,
    answer,
    advanceStep,
    waitForQuestion,
    pushLog,
  ]);

  useEffect(() => {
    if (!step) return;

    const version = stepVersionRef.current;

    const executeStep = async () => {
      try {
        if (step === 'INIT') {
          await initStep();
        } else if (step === 'PATIENT') {
          await patientStep();
        } else if (step === 'CONSULTATION') {
          await consultationStep();
        } else if (step === 'PRE_QUESTION') {
          preQuestionStep();
        } else if (step === 'QUESTION') {
          await questionStep();
        } else if (step === 'SETTLE') {
          await settleStep();
        }
      } catch (e) {
        if (stepVersionRef.current === version) {
          fail((e as Error).message);
        }
      }
    };

    if (step !== 'AWAIT_PAGE') {
      executeStep();
    }
  }, [step]);

  // Detect route change after AWAIT_PAGE (page navigation completed)
  // Only advance to PRE_QUESTION when we land on an actual questionnaire page.
  // If we land on an intermediate onboarding step (e.g. steps-overview after
  // consultation-time in MED_G flow), navigate forward until reaching a
  // questionnaire page.
  //
  // Uses the `step` state variable (not `stepRef.current`) so a stale ref from
  // a previous run can't short-circuit a fresh one: if a run starts while the
  // app is still parked on a /questionnaire/* route, the effect fires on the
  // `isRunning` change, sees `step === 'INIT'`, and no-ops until the runner
  // actually reaches AWAIT_PAGE.
  useEffect(() => {
    if (isRunning && step === 'AWAIT_PAGE') {
      if (location.pathname.startsWith('/questionnaire/')) {
        awaitedPathRef.current = null;
        advanceStep('PRE_QUESTION');
      } else if (awaitedPathRef.current !== location.pathname) {
        // Dedupe: this branch does not advance the step, so the effect
        // re-running while `location` still holds the stale pre-navigation
        // pathname would call navigateNext() a second time and push one page
        // too many (observed live: steps-overview → consultation →
        // complaints from a single introduction submit). Advance each
        // distinct intermediate pathname exactly once.
        awaitedPathRef.current = location.pathname;
        navigateNextRef.current();
      }
    }
  }, [location, advanceStep, isRunning, step]);

  return { log, run, stop, isRunning, delayMs, setDelayMs };
};
